'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBulkEntries } from '@/helpers/utils';
import { getTotalCourseDuration, getTotalCourseDurationSeconds } from '@/lib/utils/duration';
import getContentType from '@/helpers/get-content-type';
import { CardsSection } from './CardsSection';

interface SliderData {
  title?: string;
  link?: {
    title?: string;
    href?: string;
  };
  cards?: any[];
}

interface CardsSectionClientProps {
  slider?: SliderData;
  variant?: 'default' | 'grid';
  imageHeight?: number;
  fixedTitleHeight?: boolean;
}

/**
 * Helper function to format duration - preserves hours, minutes and seconds without rounding
 * Returns format like "1h 24m 26s" or "24m 26s" (not rounded)
 */
const formatDurationToMinutes = (duration: string | number | undefined): string => {
  if (!duration) return '';
  
  let totalSeconds: number;
  if (typeof duration === 'string') {
    // Handle "1h 3m 42s" or "3m 42s" format - extract total seconds
    const hoursMatch = duration.match(/(\d+)h/);
    const minutesMatch = duration.match(/(\d+)m/);
    const secondsMatch = duration.match(/(\d+)s/);
    
    const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
    const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 0;
    
    totalSeconds = hours * 3600 + minutes * 60 + seconds;
    
    // If no matches found, try to parse as a number
    if (totalSeconds === 0) {
      totalSeconds = parseInt(duration);
      if (isNaN(totalSeconds)) return '';
    }
  } else {
    totalSeconds = duration; // Assume it's in seconds
  }

  // Format with hours, minutes, and seconds
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  
  const parts: string[] = [];
  
  // Add hours if >= 1 hour
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  
  // Always show minutes if there are hours, or if minutes > 0
  if (hours > 0 || minutes > 0) {
    parts.push(`${minutes}m`);
  }
  
  // Always show seconds
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }
  
  return parts.join(' ');
};

export function CardsSectionClient({
  slider,
  variant = 'default',
  imageHeight,
  fixedTitleHeight = false,
}: CardsSectionClientProps) {
  // Extract course UIDs and learning path UIDs from slider separately —
  // each content type needs its own bulk-entries fetch (different `only` whitelist
  // and reference paths), so they can't share one contentType-hardcoded call.
  const { courseUIDs, learningPathUIDs } = useMemo(() => {
    const courseIds: string[] = [];
    const lpIds: string[] = [];
    slider?.cards?.forEach((cardItem: any) => {
      const course = cardItem?.card?.[0];
      if (!course?.uid) return;
      if (course._content_type_uid === 'learning_pathways') {
        lpIds.push(course.uid);
      } else {
        courseIds.push(course.uid);
      }
    });
    return { courseUIDs: courseIds, learningPathUIDs: lpIds };
  }, [slider]);

  // Fetch course/learning-path details from Contentstack CMS with chapters and lessons
  const { data: entryDetailsMap = new Map<string, any>() } = useQuery({
    queryKey: ['courseDetailsCMS', courseUIDs.join(','), learningPathUIDs.join(',')],
    queryFn: async () => {
      const map = new Map<string, any>();
      if (courseUIDs.length === 0 && learningPathUIDs.length === 0) {
        return map;
      }

      const fetchEntries = async (
        entryUIDs: string[],
        contentType: string,
        includeReferences: string[]
      ) => {
        if (entryUIDs.length === 0) return;

        try {
          // Note: basePath is '/academy', so API routes are prefixed
          const response = await fetch('/academy/api/bulk-entries', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              entryUIDs,
              contentType,
              includeReferences,
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API returned ${response.status}: ${errorText}`);
          }

          const entries = await response.json();
          const entriesArray = Array.isArray(entries) ? entries : [];
          entriesArray.forEach((entry: any) => {
            if (entry?.uid) map.set(entry.uid, entry);
          });
        } catch (error) {
          // Fallback to basic fetchBulkEntries if API route fails (course_details only)
          if (contentType !== 'course_details') return;
          try {
            const [courseEntries] = await fetchBulkEntries(entryUIDs);
            const courses = Array.isArray(courseEntries) ? courseEntries : [];
            courses.forEach((course: any) => {
              if (course?.uid) map.set(course.uid, course);
            });
          } catch {
            // Leave unenriched — card falls back to raw slider data
          }
        }
      };

      await Promise.all([
        fetchEntries(courseUIDs, 'course_details', ['chapters.lessons.lessons']),
        fetchEntries(learningPathUIDs, 'learning_pathways', [
          'courses.course',
          'courses.course.chapters.lessons.lessons',
        ]),
      ]);

      return map;
    },
    enabled: courseUIDs.length > 0 || learningPathUIDs.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Enrich slider cards with duration and type
  const enrichedSlider = useMemo(() => {
    if (!slider) return slider;

    return {
      ...slider,
      cards: slider.cards?.map((cardItem: any) => {
        const course = cardItem?.card?.[0];
        if (!course || !course.uid) return cardItem;

        const entryDetails = entryDetailsMap.get(course.uid);
        if (!entryDetails) return cardItem;

        const isLearningPath = course._content_type_uid === 'learning_pathways';

        let durationStr = '';
        let courseType: string;

        if (isLearningPath) {
          // Sum durations across every course inside the learning path
          const courseList = entryDetails?.courses?.course || [];
          const totalSeconds = courseList.reduce(
            (sum: number, c: any) => sum + getTotalCourseDurationSeconds(c),
            0
          );
          durationStr = totalSeconds > 0 ? formatDurationToMinutes(totalSeconds) : '';
          courseType = 'Learning Path';
        } else {
          // Calculate duration from course details using getTotalCourseDuration
          const totalDuration = getTotalCourseDuration(entryDetails);
          durationStr = formatDurationToMinutes(totalDuration);
          // Get course type from taxonomies
          courseType = getContentType(entryDetails.taxonomies || [], 'content_type') || 'Course';
        }

        // Enrich the course object with duration and taxonomies from CMS
        return {
          ...cardItem,
          card: [{
            ...course,
            duration: durationStr,
            courseType,
            taxonomies: entryDetails.taxonomies || course.taxonomies || [],
          }],
        };
      }),
    };
  }, [slider, entryDetailsMap]);

  return (
    <CardsSection
      slider={enrichedSlider}
      variant={variant}
      imageHeight={imageHeight}
      fixedTitleHeight={fixedTitleHeight}
    />
  );
}

